import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Key, Plus, X, Copy, RefreshCw, Trash2, ShieldCheck, CheckCircle2 } from "lucide-react";

export default function OIDCClientPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);   // {client_id, client_secret, ...} shown once
  const [rotated, setRotated] = useState(null);
  const [copied, setCopied] = useState("");
  const [form, setForm] = useState({
    name: "",
    description: "",
    redirect_uris: "",
    scopes: "openid profile email",
  });

  const load = async () => {
    try {
      const res = await api.get("/admin/oidc/clients");
      setData(res.data);
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || (e && e.message) || "Unknown error");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setCreated(null);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        redirect_uris: form.redirect_uris.split("\n").map((s) => s.trim()).filter(Boolean),
        scopes: form.scopes.split(/\s+/).filter(Boolean),
      };
      const res = await api.post("/admin/oidc/clients", payload);
      setCreated(res.data);
      setForm({ name: "", description: "", redirect_uris: "", scopes: "openid profile email" });
      await load();
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ") : String(d || e.message);
      alert("Failed: " + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const rotate = async (cid) => {
    if (!confirm(`Rotate secret for ${cid}? Old secret stops working immediately.`)) return;
    try {
      const res = await api.post(`/admin/oidc/clients/${cid}/rotate-secret`);
      setRotated(res.data);
    } catch (e) {
      alert("Rotate failed: " + (e.message || ""));
    }
  };

  const remove = async (cid) => {
    if (!confirm(`Permanently delete client ${cid}?`)) return;
    try {
      await api.delete(`/admin/oidc/clients/${cid}`);
      await load();
    } catch (e) {
      alert("Delete failed: " + (e.message || ""));
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      // ignore
    }
  };

  if (err && !data) {
    return (
      <div data-testid="oidc-panel-error" className="glass-strong rounded-xl p-6 border border-red-500/30 text-sm text-red-300 mb-8">
        Unable to load OIDC clients · {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass-strong rounded-xl p-8 border border-white/10 text-center text-xs text-zinc-500 tracking-armada mb-8">
        SCANNING CLIENT REGISTRY…
      </div>
    );
  }

  return (
    <div data-testid="oidc-panel" className="glass-strong rounded-2xl p-6 lg:p-8 border border-violet-400/20 mb-8 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 70% 20%, rgba(168,85,247,0.5), transparent 60%)" }}
      />
      <div className="relative">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Key className="w-4 h-4 text-violet-300" />
              <div className="text-[10px] tracking-armada text-violet-300 font-bold">
                OIDC CLIENT MANAGEMENT · {data.active}/{data.total} ACTIVE
              </div>
            </div>
            <h3 className="text-2xl md:text-3xl font-black tracking-tighter text-white">
              Sovereign Handshakes
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Register OIDC clients (Nessa, Heritage, child apps) to authenticate against this SSO hub. Each client has a unique client_id + client_secret pair.
            </p>
          </div>
          <button
            onClick={() => { setShowForm(!showForm); setCreated(null); }}
            data-testid="oidc-toggle-form"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-violet-400/15 hover:bg-violet-400/25 border border-violet-400/50 text-violet-200 text-xs font-bold tracking-wider uppercase transition"
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "Register Client"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} data-testid="oidc-form" className="mb-6 p-5 rounded-lg border border-violet-400/30 bg-black/40 space-y-3">
            <label className="block">
              <div className="text-[10px] tracking-armada text-violet-300 font-bold mb-1">CLIENT NAME</div>
              <input
                required data-testid="oidc-input-name" type="text"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nessa · Heritage · SlayMarie · Vanguard"
                className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white text-sm focus:border-violet-400/60 focus:outline-none"
              />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-armada text-violet-300 font-bold mb-1">DESCRIPTION (OPTIONAL)</div>
              <input
                data-testid="oidc-input-desc" type="text"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Nessa Mercy Hull — OIDC handshake"
                className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white text-sm focus:border-violet-400/60 focus:outline-none"
              />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-armada text-violet-300 font-bold mb-1">REDIRECT URIS (ONE PER LINE, MUST BE HTTPS OR LOCALHOST)</div>
              <textarea
                required data-testid="oidc-input-redirects"
                value={form.redirect_uris} onChange={(e) => setForm({ ...form, redirect_uris: e.target.value })}
                placeholder="https://nessa.eigooinc.net/auth/callback&#10;https://nessa.eigooinc.net/oidc/callback"
                rows={3}
                className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white text-sm focus:border-violet-400/60 focus:outline-none font-mono"
              />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-armada text-violet-300 font-bold mb-1">SCOPES (SPACE-SEPARATED)</div>
              <input
                data-testid="oidc-input-scopes" type="text"
                value={form.scopes} onChange={(e) => setForm({ ...form, scopes: e.target.value })}
                className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white text-sm focus:border-violet-400/60 focus:outline-none font-mono"
              />
            </label>
            <button
              type="submit" disabled={submitting} data-testid="oidc-submit"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-gradient-to-r from-violet-400 to-violet-500 text-black text-xs font-bold tracking-wider uppercase hover:from-violet-300 hover:to-violet-400 transition disabled:opacity-50"
            >
              <ShieldCheck className="w-4 h-4" />
              {submitting ? "Forging Handshake…" : "Forge Client Credentials"}
            </button>
          </form>
        )}

        {(created || rotated) && (
          <div data-testid="oidc-secret-banner" className="mb-6 p-5 rounded-lg border border-amber-400/50 bg-amber-400/10">
            <div className="text-[10px] tracking-armada text-amber-300 font-bold mb-2">
              ⚠️ COPY NOW · CLIENT SECRET SHOWN ONCE
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-zinc-400 w-24">client_id:</span>
                <code className="flex-1 px-2 py-1 rounded bg-black/60 text-violet-200 select-all break-all">
                  {(created || rotated).client_id}
                </code>
                <button onClick={() => copyToClipboard((created || rotated).client_id, "id")} className="p-1.5 rounded hover:bg-white/10">
                  {copied === "id" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-400 w-24">client_secret:</span>
                <code className="flex-1 px-2 py-1 rounded bg-black/60 text-amber-200 select-all break-all">
                  {(created || rotated).client_secret}
                </code>
                <button onClick={() => copyToClipboard((created || rotated).client_secret, "secret")} className="p-1.5 rounded hover:bg-white/10">
                  {copied === "secret" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
                </button>
              </div>
            </div>
            <button
              onClick={() => { setCreated(null); setRotated(null); }}
              className="mt-3 text-[10px] tracking-armada text-amber-300 hover:text-amber-100"
            >
              I've stored it — dismiss
            </button>
          </div>
        )}

        <div className="space-y-2">
          {(data.clients || []).length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-white/10 rounded-md">
              No OIDC clients yet. Register your first to start federating logins to Nessa or other hulls.
            </div>
          ) : (
            (data.clients || []).map((c) => (
              <div key={c.client_id} data-testid={"oidc-client-" + c.client_id} className="p-4 rounded-lg border border-white/5 bg-black/30 hover:border-violet-400/30 transition">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-white">{c.name}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${c.is_active ? "bg-emerald-400/20 text-emerald-200 border border-emerald-400/40" : "bg-zinc-700/40 text-zinc-400 border border-zinc-600/40"}`}>
                        {c.is_active ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>
                    {c.description && <div className="text-[11px] text-zinc-500 mt-0.5">{c.description}</div>}
                    <div className="text-[10px] text-zinc-500 mt-1">
                      <code className="text-violet-200">{c.client_id}</code>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Redirects · <span className="text-zinc-400">{(c.redirect_uris || []).join(" · ")}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Scopes · <span className="text-zinc-400">{(c.scopes || []).join(" ")}</span> · Usage {c.usage_count || 0}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copyToClipboard(c.client_id, c.client_id)} title="Copy client_id" className="p-1.5 rounded hover:bg-white/10">
                      {copied === c.client_id ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
                    </button>
                    <button onClick={() => rotate(c.client_id)} title="Rotate secret" className="p-1.5 rounded hover:bg-amber-400/10">
                      <RefreshCw className="w-3.5 h-3.5 text-amber-300" />
                    </button>
                    <button onClick={() => remove(c.client_id)} title="Delete client" className="p-1.5 rounded hover:bg-red-500/10">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
